<template>
    <nio-modal
        name="payment"
        size="md"
        :title="modalTitle"
        ref="modal"
        @closed="onClosed"
        @open="onOpen"
        @opened="onOpened"
    >
        <div class="space-y-6">
            <div class="flex space-x-6">
                <div class="w-1/2 flex-col space-y-6">
                    <nio-input-group :label="$t('payments.paymentModal.contact')">
                        <nio-input
                            :disabled="true"
                            :value="payment.contact.fullName"
                            v-if="contactIsPrefilled"
                        />

                        <contact-select
                            ref="contact"
                            v-model="payment.contact_id"
                            v-else
                        />
                    </nio-input-group>

                    <nio-input-group :label="$t('payments.paymentModal.amount')">
                        <nio-input
                            :locale="authUser.locale"
                            mask="money"
                            ref="amount"
                            v-model="payment.amount"
                        />
                    </nio-input-group>

                    <nio-input-group :label="$t('payments.paymentModal.invoiceNumber')">
                        <nio-input v-model="payment.invoice_number" />
                    </nio-input-group>
                </div>

                <div class="w-1/2 flex justify-center items-center p-4 border border-gray-200 rounded-lg bg-gray-100">
                    <file-upload
                        :compact="true"
                        :file-types="['pdf', 'png', 'jpg', 'jpeg']"
                        :is-uploading="isUploading"
                        @error="setIsFileInvalid"
                        v-model="file"
                        v-if="showFileUpload"
                    />

                    <div class="flex flex-col justify-center items-center py-2 | transition duration-300" v-else>
                        <div class="px-4 text-center">
                            <div>
                                <nio-icon class="h-11 w-11 text-gray-600" name="light/office-file-xls" />
                            </div>
                            <div class="mt-4">
                                {{ payment.file_name }}
                            </div>

                            <nio-button-group class="mt-4">
                                <nio-button @click="existingFileDeleted = true">
                                    {{ $t('imports.upload.removeFile') }}
                                </nio-button>

                                <nio-button @click="previewFile">
                                    {{ $t('imports.upload.viewFile') }}
                                </nio-button>
                            </nio-button-group>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <template #footer>
            <nio-button @click="close">
                {{ $t('global.general.cancel') }}
            </nio-button>

            <nio-button
                color="blue"
                :disabled="!valid"
                :loading="$wait.is('saving.payment')"
                @click="triggerPrimaryAction"
            >
                {{ primaryActionText }}
            </nio-button>
        </template>

        <confirm-modal
            :opened.sync="sendLinkModalOpened"
            :title="$t('payments.paymentModal.promptSendPaymentLinkModal.header')"
            @confirm="openSendPaymentLinkModal"
            @deny="close"
        >
            <p slot="message">
                {{ $t('payments.paymentModal.promptSendPaymentLinkModal.message') }}
            </p>
        </confirm-modal>
    </nio-modal>
</template>

<script>
    import { mapActions, mapState } from 'vuex';
    import ConfirmModal from '@/components/Modals/ConfirmModal.vue';
    import FileUpload from '@/components/FileUpload.vue';
    import Payment from '@/entities/Payment.js';
    import Vapor from 'laravel-vapor';
    import ContactSelect from '@/components/ContactSelect.vue';

    export default {
        components: {
            ContactSelect,
            ConfirmModal,
            FileUpload,
        },

        data() {
            return {
                existingFileDeleted: false,
                file: null,
                isFileInvalid: false,
                isUploading: false,
                payment: new Payment(),
                savedPayment: null, // Useful only to pass as prop when opening sendPaymentLinkModal
                sendLinkModalOpened: false,
            };
        },

        computed: {
            ...mapState([
                'authUser',
            ]),

            valid() {
                return this.payment.amount >= 1 &&
                    this.contactId &&
                    this.payment.invoice_number &&
                    !this.isFileInvalid;
            },

            editing() {
                return !!this.payment.id;
            },

            primaryActionText() {
                return this.editing
                    ? this.$t('global.general.save')
                    : this.$t('global.general.create');
            },

            contactIsPrefilled() {
                return !!this.payment.contact.isExisting();
            },

            contactId() {
                return this.payment.contact.id || this.payment.contact_id;
            },

            modalTitle() {
                return this.editing
                    ? this.$t('payments.paymentModal.title.edit')
                    : this.$t('payments.paymentModal.title.create');
            },

            showFileUpload() {
                return !this.editing || !this.payment.file_name || this.existingFileDeleted;
            },

            amountInCents() {
                return this.payment.amount * 100;
            },
        },

        methods: {
            ...mapActions('payment', [
                'createPayment',
                'updatePayment',
            ]),

            triggerPrimaryAction() {
                if (this.editing) {
                    this.triggerUpdatePayment();
                    return;
                }

                this.triggerCreatePayment();
            },

            async triggerCreatePayment() {
                this.$wait.start('saving.payment');

                let payload = {
                    amount: this.amountInCents,
                    contact_id: this.contactId,
                    invoice_number: this.payment.invoice_number,
                };

                if (this.file) {
                    this.isUploading = true;

                    try {
                        const fileUploadResponse = await this.uploadFileToS3AndReturnUpdatedPayload(payload);

                        payload = {
                            ...payload,
                            file_key: fileUploadResponse.key,
                            file_name: this.file.name,
                            file_content_type: this.file.type,
                        };
                    } catch (error) {
                        this.$notify.show({
                            type: 'error',
                            message: this.$t('payments.paymentModal.notifications.error.uploadingFile'),
                        });

                        this.$wait.end('saving.payment');

                        return;
                    } finally {
                        this.isUploading = false;
                    }
                }

                try {
                    const response = await this.createPayment(payload);

                    this.savedPayment = new Payment(response);
                    this.sendLinkModalOpened = true;
                } catch (error) {
                    this.$notify.show({
                        type: 'error',
                        message: this.$t('payments.paymentModal.notifications.error.create'),
                    });
                }

                this.$wait.end('saving.payment');
            },

            async triggerUpdatePayment() {
                this.$wait.start('saving.payment');

                let payload = {
                    amount: this.amountInCents,
                    invoice_number: this.payment.invoice_number,
                };

                if (this.file) {
                    try {
                        const response = await this.uploadFileToS3AndReturnUpdatedPayload(payload);

                        payload = {
                            ...payload,
                            file_key: response.key,
                            file_name: this.file.name,
                            file_content_type: this.file.type,
                        };
                    } catch (error) {
                        this.$notify.show({
                            type: 'error',
                            message: this.$t('payments.paymentModal.notifications.error.uploadingFile'),
                        });

                        this.$wait.end('saving.payment');

                        return;
                    }
                } else if (this.existingFileDeleted) {
                    payload = {
                        ...payload,
                        file_key: null,
                        file_name: null,
                        file_content_type: null,
                    };
                }

                try {
                    const response = await this.updatePayment({
                        id: this.payment.id,
                        payload,
                    });

                    this.$notify.show({
                        type: 'success',
                        message: this.$t('payments.paymentModal.notifications.success.update'),
                    });

                    if (!response.sent_at) {
                        this.savedPayment = new Payment(response);

                        this.sendLinkModalOpened = true;
                    } else {
                        this.close();
                    }
                } catch (error) {
                    this.$notify.show({
                        type: 'error',
                        message: this.$t('payments.paymentModal.notifications.error.update'),
                    });
                }

                this.$wait.end('saving.payment');
            },

            openSendPaymentLinkModal() {
                this.$modal.open('sendPaymentLink', { payment: this.savedPayment });
                this.close();
            },

            async uploadFileToS3AndReturnUpdatedPayload() {
                return Vapor.store(this.file, {
                    bucket: process.env.VUE_APP_S3_BUCKET,
                    baseURL: this.$axios.defaults.baseURL,
                    headers: this.$axios.defaults.headers.common,
                });
            },

            previewFile() {
                window.open(this.payment.file_path);
            },

            setIsFileInvalid(newValue) {
                this.isFileInvalid = newValue;
            },

            onOpen({ contact, payment } = {}) {
                const amountInDollars = payment ? payment.amount / 100 : 0;

                this.payment.fill({
                    ...payment,
                    contact,
                    amount: amountInDollars,
                });
            },

            onOpened() {
                if (!this.contactIsPrefilled) {
                    this.$refs.contact?.open();
                } else if (!this.editing) {
                    this.$refs.amount?.focus();
                }
            },

            onClosed() {
                this.$reset();
            },

            close() {
                this.$refs.modal.close();
            },
        },
    };
</script>
